import * as THREE from 'three';
import { Brush, Evaluator, ADDITION, SUBTRACTION, INTERSECTION } from 'three-bvh-csg';

/**
 * CSG bridge for the Sketch engine.
 *
 * Wraps three-bvh-csg (already a project dependency powering the BIM solid
 * kernel) at the GEOMETRY level: no THREE.Mesh instances or matrices, just
 * world-space BufferGeometry in/out. The Sketch engine uses it for Push/Pull
 * unions/subtractions and hole punching; the analytic shell is rebuilt
 * afterwards by meshToSolid (watertight guard lives there).
 */

const evaluator = new Evaluator();
// Split by material groups is irrelevant here (single-material shells).
// Only POSITION is carried through the boolean: normals are re-derived from
// scratch by every consumer (meshToSolid, triangulate), and interpolating
// them across split triangles only adds drift. The trimmed attribute list
// also avoids the evaluator's "undefined array" crash on brushes missing a
// UV channel (solidToGeometry emits position+normal only).
evaluator.useGroups = false;
evaluator.attributes = ['position'];

const hasTriangles = (geometry: THREE.BufferGeometry): boolean => {
    const pos = geometry.getAttribute('position');
    return Boolean(pos) && pos.count >= 3;
};

const toBrush = (geometry: THREE.BufferGeometry): Brush => {
    const brush = new Brush(geometry);
    brush.updateMatrixWorld();
    return brush;
};

const RUN = (a: THREE.BufferGeometry, b: THREE.BufferGeometry, op: typeof ADDITION | typeof SUBTRACTION | typeof INTERSECTION): THREE.BufferGeometry => {
    // Degenerate side guard: a union with an empty shell is the other shell;
    // a subtraction against one is a no-op. Feeding an empty brush to the
    // evaluator would throw deep inside its attribute preparation.
    if (!hasTriangles(a) && !hasTriangles(b)) return new THREE.BufferGeometry();
    if (op === ADDITION && !hasTriangles(a)) return b.clone();
    if (op === ADDITION && !hasTriangles(b)) return a.clone();
    if (op === SUBTRACTION && (!hasTriangles(b) || !hasTriangles(a))) return a.clone();
    if (op === INTERSECTION && (!hasTriangles(b) || !hasTriangles(a))) return new THREE.BufferGeometry();
    const result = evaluator.evaluate(toBrush(a), toBrush(b), op);
    const geometry = result.geometry.clone();
    geometry.computeBoundingSphere();
    return geometry;
};

/** Boolean union (A + B). */
export const csgUnion = (a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry =>
    RUN(a, b, ADDITION);

/** Boolean subtraction (A - B). */
export const csgSubtract = (a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry =>
    RUN(a, b, SUBTRACTION);

/** Boolean intersection (A ∩ B) — the Solid Tools' Intersect / Split core. */
export const csgIntersect = (a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry =>
    RUN(a, b, INTERSECTION);
